import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, apiError } from '@/lib/api';
import { toast } from 'sonner';
import { cleanOverride } from './PriceControls';

const toDraft = data => ({ revision: data.revision, manual_entries: data.manual_entries, overrides: data.overrides, settings: data.settings });

export const usePriceList = () => {
  const [source, setSource] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  const refresh = useCallback(async (discard = false) => {
    try {
      const { data } = await api.get('/admin/price-list');
      setSource(data); setError('');
      if (!dirtyRef.current || discard) { setDraft(toDraft(data)); setDirty(false); }
    } catch (e) { setError(apiError(e)); }
  }, []);
  useEffect(() => {
    refresh();
    const timer = setInterval(() => refresh(), 45000);
    const focus = () => refresh();
    window.addEventListener('focus', focus);
    return () => { clearInterval(timer); window.removeEventListener('focus', focus); };
  }, [refresh]);
  useEffect(() => {
    const protect = e => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', protect);
    return () => window.removeEventListener('beforeunload', protect);
  }, []);
  const entries = useMemo(() => {
    if (!source || !draft) return [];
    const courses = source.entries.filter(e => e.source === 'course').map(e => ({ ...e, ...cleanOverride(e.base_values), ...cleanOverride(draft.overrides[e.id]) }));
    const availableManual = new Set(source.entries.filter(e => e.source !== 'course').map(e => e.id));
    const manuals = draft.manual_entries.filter(e => !e.id.startsWith('offer-') || availableManual.has(e.id))
      .map(e => ({ ...e, source: e.id.startsWith('offer-') ? 'seed' : 'manual', system_price: e.price, ...cleanOverride(draft.overrides[e.id]) }));
    return [...courses, ...manuals].map(e => ({ ...e, effective_price: e.flyer_price ?? e.system_price,
      layout_excluded: !!(draft.settings.learning_path && draft.settings.sections[e.category]?.panel === 'right'
        && e.category === 'gruppentraining' && e.id !== 'offer-newcomer' && !e.title.toLowerCase().includes('newcomer')) }))
      .sort((a, b) => a.order-b.order || a.title.localeCompare(b.title, 'de'));
  }, [source, draft]);
  const edit = (id, values) => {
    const course = entries.find(e => e.id === id)?.source === 'course';
    setDraft(d => course ? { ...d, overrides: { ...d.overrides, [id]: { ...d.overrides[id], ...values } } }
      : { ...d, manual_entries: d.manual_entries.map(e => e.id === id ? { ...e, ...values } : e) });
    setDirty(true);
  };
  const settings = values => { setDraft(d => ({ ...d, settings: { ...d.settings, ...values } })); setDirty(true); };
  const add = () => {
    const id = `manual-${crypto.randomUUID()}`;
    setDraft(d => ({ ...d, manual_entries: [...d.manual_entries, { id, title: 'Neue Leistung', category: 'einzelcoaching',
      short_description: '', price: null, flyer_price: null, duration: '', price_prefix: '', price_unit: '', additional_info: '',
      order: 500, visible: true, show_on_flyer: false, highlighted: false }] }));
    setDirty(true); return id;
  };
  const remove = id => { setDraft(d => ({ ...d, manual_entries: d.manual_entries.filter(e => e.id !== id) })); setDirty(true); };
  const reset = id => { setDraft(d => { const overrides = { ...d.overrides }; delete overrides[id]; return { ...d, overrides }; }); setDirty(true); };
  const reorder = (items) => {
    const chosen = new Set(items);
    const category = entries.find(e => chosen.has(e.id))?.category;
    let index = 0;
    const allIds = entries.filter(e => e.category === category).map(e => chosen.has(e.id) ? items[index++] : e.id);
    const positions = Object.fromEntries(allIds.map((id, index) => [id, index*10]));
    const courseIds = new Set(entries.filter(e => e.source === 'course').map(e => e.id));
    setDraft(d => {
      const overrides = { ...d.overrides };
      Object.entries(positions).forEach(([id, order]) => { if (courseIds.has(id)) overrides[id] = { ...overrides[id], order }; });
      return { ...d, overrides, manual_entries: d.manual_entries.map(e => positions[e.id] === undefined ? e : { ...e, order: positions[e.id] }) };
    }); setDirty(true);
  };
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put('/admin/price-list', draft); setSource(data); setDraft(toDraft(data)); setDirty(false); toast.success('Preisliste gespeichert'); }
    catch (e) { toast.error(apiError(e)); setError(apiError(e)); }
    finally { setSaving(false); }
  };
  return { source, draft, entries, dirty, error, saving, refresh, edit, settings, add, remove, reset, reorder, save };
};